import { diffChars, diffLines, diffWords, createTwoFilesPatch } from 'diff';
import { byId, isAr, message, copyText, saveBlob, trackEvent } from './extra-common.js';

const left = byId('td-left');
const right = byId('td-right');
const output = byId('td-output');
const modeSelect = byId('td-mode');
const viewSelect = byId('td-view');

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function compare() {
  trackEvent('tool_started', 'text-diff');
  try {
    const leftVal = left?.value ?? '';
    const rightVal = right?.value ?? '';
    const mode = modeSelect?.value || 'line';
    const view = viewSelect?.value || 'split';

    if (!leftVal && !rightVal) {
      if (output) output.replaceChildren();
      message('td-summary', isAr ? 'يرجى إدخال نص في أحد الحقلين للمقارنة.' : 'Please enter text in at least one field to compare.', 'info');
      return;
    }

    if (view === 'split' && mode === 'line') {
      renderSplitDiff(leftVal, rightVal);
    } else {
      renderUnifiedDiff(leftVal, rightVal, mode);
    }

    trackEvent('tool_completed', 'text-diff');
  } catch {
    message('td-summary', isAr ? 'تعذر إجراء المقارنة.' : 'Comparison failed.', 'error');
    trackEvent('tool_failed', 'text-diff');
  }
}

function renderUnifiedDiff(leftVal, rightVal, mode) {
  const method = ({ line: diffLines, word: diffWords, char: diffChars })[mode] || diffLines;
  const parts = method(leftVal, rightVal, { newlineIsToken: true });

  output.className = 'diff-output';
  output.replaceChildren();

  let added = 0;
  let removed = 0;

  for (const part of parts) {
    if (part.added) added += part.count || 0;
    if (part.removed) removed += part.count || 0;

    const span = document.createElement('span');
    if (part.added) span.className = 'diff-add';
    if (part.removed) span.className = 'diff-remove';
    span.textContent = part.value;
    output.append(span);
  }

  const summary = isAr
    ? `الوضع الموحد · إضافات: ${added} · محذوفات: ${removed}`
    : `Unified mode · Added: ${added} · Removed: ${removed}`;
  message('td-summary', summary, 'success');
}

function renderSplitDiff(leftVal, rightVal) {
  const lineDiffs = diffLines(leftVal, rightVal);
  output.className = 'diff-split-container';
  output.replaceChildren();

  let addedCount = 0;
  let removedCount = 0;

  // Build paired rows
  const leftLines = [];
  const rightLines = [];

  let pendingRemoved = [];

  for (const part of lineDiffs) {
    const lines = part.value.replace(/\r\n/g, '\n').split('\n');
    if (lines[lines.length - 1] === '') lines.pop();

    if (part.removed) {
      removedCount += lines.length;
      pendingRemoved.push(...lines);
    } else if (part.added) {
      addedCount += lines.length;
      // Pair pending removed with added
      const maxLen = Math.max(pendingRemoved.length, lines.length);
      for (let i = 0; i < maxLen; i++) {
        const rem = pendingRemoved[i] ?? null;
        const add = lines[i] ?? null;

        if (rem !== null && add !== null) {
          // Inline char diff for precision
          const charDiffs = diffWords(rem, add);
          const leftHtml = charDiffs
            .filter(c => !c.added)
            .map(c => c.removed ? `<span class="diff-char-remove">${escapeHtml(c.value)}</span>` : escapeHtml(c.value))
            .join('');
          const rightHtml = charDiffs
            .filter(c => !c.removed)
            .map(c => c.added ? `<span class="diff-char-add">${escapeHtml(c.value)}</span>` : escapeHtml(c.value))
            .join('');

          leftLines.push({ html: leftHtml, cls: 'diff-remove' });
          rightLines.push({ html: rightHtml, cls: 'diff-add' });
        } else if (rem !== null) {
          leftLines.push({ html: escapeHtml(rem), cls: 'diff-remove' });
          rightLines.push({ html: '&nbsp;', cls: 'diff-empty' });
        } else {
          leftLines.push({ html: '&nbsp;', cls: 'diff-empty' });
          rightLines.push({ html: escapeHtml(add), cls: 'diff-add' });
        }
      }
      pendingRemoved = [];
    } else {
      // Flush any lone removed lines
      for (const rem of pendingRemoved) {
        leftLines.push({ html: escapeHtml(rem), cls: 'diff-remove' });
        rightLines.push({ html: '&nbsp;', cls: 'diff-empty' });
      }
      pendingRemoved = [];

      // Add normal lines
      for (const line of lines) {
        leftLines.push({ html: escapeHtml(line), cls: '' });
        rightLines.push({ html: escapeHtml(line), cls: '' });
      }
    }
  }

  // Flush any trailing removed
  for (const rem of pendingRemoved) {
    leftLines.push({ html: escapeHtml(rem), cls: 'diff-remove' });
    rightLines.push({ html: '&nbsp;', cls: 'diff-empty' });
  }

  // Render left column
  const leftCol = document.createElement('div');
  leftCol.className = 'diff-split-col';
  const leftHeader = document.createElement('div');
  leftHeader.className = 'diff-split-header';
  leftHeader.textContent = isAr ? 'النسخة الأصلية (Original)' : 'Original Text';
  leftCol.appendChild(leftHeader);

  leftLines.forEach((item, idx) => {
    const lineEl = document.createElement('div');
    lineEl.className = `diff-line ${item.cls}`.trim();
    lineEl.innerHTML = item.html || '&nbsp;';
    leftCol.appendChild(lineEl);
  });

  // Render right column
  const rightCol = document.createElement('div');
  rightCol.className = 'diff-split-col';
  const rightHeader = document.createElement('div');
  rightHeader.className = 'diff-split-header';
  rightHeader.textContent = isAr ? 'النسخة المعدلة (Modified)' : 'Modified Text';
  rightCol.appendChild(rightHeader);

  rightLines.forEach((item, idx) => {
    const lineEl = document.createElement('div');
    lineEl.className = `diff-line ${item.cls}`.trim();
    lineEl.innerHTML = item.html || '&nbsp;';
    rightCol.appendChild(lineEl);
  });

  output.appendChild(leftCol);
  output.appendChild(rightCol);

  const summary = isAr
    ? `عرض متجاور · أسطر مضافة: ${addedCount} · أسطر محذوفة: ${removedCount}`
    : `Side-by-side mode · Added lines: ${addedCount} · Removed lines: ${removedCount}`;
  message('td-summary', summary, 'success');
}

function getPatchContent() {
  const leftVal = left?.value ?? '';
  const rightVal = right?.value ?? '';
  return createTwoFilesPatch('original.txt', 'modified.txt', leftVal, rightVal, 'original', 'modified');
}

function exportPatch() {
  const patch = getPatchContent();
  const blob = new Blob([patch], { type: 'text/plain;charset=utf-8' });
  saveBlob(blob, 'changes.patch');
  message('td-summary', isAr ? 'تم تنزيل ملف الترقيع changes.patch.' : 'Downloaded changes.patch file.', 'success');
}

async function copyPatchToClipboard() {
  try {
    const patch = getPatchContent();
    await copyText(patch);
    message('td-summary', isAr ? 'تم نسخ محتوى ملف الترقيع (Patch) إلى الحافظة.' : 'Copied patch to clipboard.', 'success');
  } catch {
    message('td-summary', isAr ? 'تعذر النسخ إلى الحافظة.' : 'Could not copy to clipboard.', 'error');
  }
}

byId('td-compare')?.addEventListener('click', compare);
byId('td-mode')?.addEventListener('change', compare);
byId('td-view')?.addEventListener('change', compare);
byId('td-export-patch')?.addEventListener('click', exportPatch);
byId('td-copy-patch')?.addEventListener('click', copyPatchToClipboard);
byId('td-swap')?.addEventListener('click', () => {
  if (left && right) {
    const temp = left.value;
    left.value = right.value;
    right.value = temp;
    compare();
  }
});
